export { type Props } from "~/components/minicart/Cart"
import type { Props } from "~/components/minicart/Cart"
import clx from "~/sdk/clx"
import { useDisplayCart, useUI } from "~/sdk/useUI"
import { lazy, Suspense, useEffect } from "react"

const Cart = lazy(() => import('~/components/minicart/Cart'))

/**
 * Right-side drawer that opens when `displayCart.value = true`.
 *
 * Reactivity: `useDisplayCart()` subscribes to the @tanstack/react-store
 * backing of the `displayCart` signal — reading `.value` in a render body is
 * NOT reactive, so we need this hook to re-render when AddToCartButton flips
 * the signal. The Suspense + `lazy(Cart)` keeps the heavy cart bundle (and
 * its useCart subscription / item rendering) off the initial paint of every
 * page; it's only fetched the first time the drawer opens.
 *
 * UX niceties:
 * - Lock the page scroll while the drawer is open, so the underlying page
 *   doesn't scroll behind the panel
 * - ESC closes the drawer (standard dialog behavior)
 * - Click on the dark backdrop closes the drawer
 */
export default function Minicart(props: Props) {
    const open = useDisplayCart()
    const { displayCart } = useUI()

    useEffect(() => {
        if (!open) return

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        function onKey(e: KeyboardEvent) {
            if (e.key === 'Escape') displayCart.value = false
        }
        window.addEventListener('keydown', onKey)

        return () => {
            document.body.style.overflow = previousOverflow
            window.removeEventListener('keydown', onKey)
        }
    }, [open, displayCart])

    return (
        <>
            <div
                aria-hidden={!open}
                onClick={() => (displayCart.value = false)}
                className={clx(
                    // z must sit above ALL page content (cards/flags/sticky use
                    // up to z-50), otherwise those elements show through the
                    // backdrop undimmed ("highlighted"). Panel is z-[101] below.
                    'fixed inset-0 z-[100] bg-black/40 transition-opacity duration-200',
                    open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
                )}
            />

            <aside
                role='dialog'
                aria-modal='true'
                aria-label='Sacola'
                aria-hidden={!open}
                className={clx(
                    'fixed top-0 right-0 bottom-0 z-[101] w-screen max-w-[450px] transition-transform duration-200 flex',
                    open ? 'translate-x-0' : 'translate-x-full pointer-events-none',
                )}
            >
                <Suspense
                    fallback={
                        <div className='w-full flex items-center justify-center bg-neutral-100'>
                            <span className='loading loading-spinner loading-lg text-salmon' />
                        </div>
                    }
                >
                    {open && <Cart {...props} />}
                </Suspense>
            </aside>
        </>
    )
}
